import React, { useEffect } from "react";
import RequireConferenceRole from './components/ui/RequireConferenceRole';
import { ConferenceProvider } from './contexts/ConferenceContext';
import { PageLoading } from './components/ui/Skeleton';
import { BrowserRouter, Routes as RouterRoutes, Route, useNavigate, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./pages/NotFound";
import AuthorSubmissionPortal from './pages/author-submission-portal';
import UserDashboard from './pages/user-dashboard';
import ConferenceSetupWizard from './pages/conference-setup-wizard';
import ConferenceLandingPage from './pages/conference-landing-page';
import UserRegistration from './pages/user-registration';
import PublicConferenceBoard from './pages/public-conference-board';
import UserSignIn from './pages/user-sign-in';
import UserProfileManagement from './pages/user-profile-management';

import PaperReviewInterface from './pages/paper-review-interface';
import ConferenceSubmissionsOverview from './pages/conference-submissions-overview';
import MySubmissions from './pages/my-submissions';
import ReviewSchemaBuilder from './pages/review-schema-builder';
import SubmissionTrackingDashboard from './pages/submission-tracking-dashboard';
import ConferenceManagementDashboard from './pages/conference-management-dashboard';

import NotificationManagementScreen from './pages/notification-management-screen';
import NotificationsInbox from './pages/notifications';
import BillingPage from './pages/billing';
import ReviewerDashboard from './pages/reviewer-dashboard';
import AuthorReviewResultsDashboard from './pages/author-review-results-dashboard';
import ModernLandingPage from './pages/modern-landing-page';
import StreamlinedReviewWorkspace from './pages/streamlined-review-workspace';
import EmailTemplateManagementSystem from './pages/email-template-management-system';
import ManualReviewerAssignmentInterface from './pages/manual-reviewer-assignment-interface';
import MasterAdminDashboard from './pages/master-admin-dashboard';
import AdminConferenceManagementInterface from './pages/admin-conference-management-interface';
import AdminUserManagementSystem from './pages/admin-user-management-system';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import CheckEmail from './pages/check-email';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ReConsentBanner from './components/ReConsentBanner';

// Auth redirect handler component
const AuthRedirectHandler = ({ children }) => {
  const { user, userProfile, loading } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) return;

    // Define public routes that don't require auth
    const publicRoutes = [
      '/',
      '/modern-landing-page',
      '/public-conference-board',
      '/conference-landing-page',
      '/user-registration',
      '/user-sign-in',
      '/check-email',
      '/forgot-password',
      '/reset-password'
    ];

    const isPublicRoute = publicRoutes?.includes(location?.pathname);
    const isAdmin = userProfile?.isPlatformAdmin ?? userProfile?.roles?.includes('admin');

    /* A signed-in person landing on the marketing page or the sign-in form is
       almost always just opening the app — the bookmark is "/", the browser
       autocompletes to "/", the logo links to "/". Sending them to the
       dashboard skips a page that exists to explain the product to people who
       do not have an account yet.
       `replace` so Back doesn't bounce them straight back here. Reachable
       deliberately at /modern-landing-page, which stays out of this list so
       there is still a way to look at the marketing page while signed in. */
    const entryRoutes = ['/', '/user-sign-in'];
    if (user && entryRoutes.includes(location?.pathname)) {
      navigate(isAdmin ? '/master-admin-dashboard' : '/user-dashboard', { replace: true });
      return;
    }

    // If user is not authenticated and on a protected route, redirect to public board
    if (!user && !isPublicRoute) {
      navigate('/public-conference-board', { replace: true });
      return;
    }
  }, [user, loading, location?.pathname, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <PageLoading label={t('actions.loading')} />
      </div>
    );
  }

  return (
    <>
      {children}
      <ReConsentBanner />
    </>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <ScrollToTop />
          {/* Conference scope wraps the router so the chrome above every page
              knows which conference is in play and what this person may do in
              it. Roles are per conference, so this cannot live on the profile. */}
          <ConferenceProvider>
          <AuthRedirectHandler>
            <RouterRoutes>
              {/* Public Routes */}
              <Route path="/" element={<ModernLandingPage />} />
              <Route path="/modern-landing-page" element={<ModernLandingPage />} />
              <Route path="/public-conference-board" element={<PublicConferenceBoard />} />
              <Route path="/conference-landing-page" element={<ConferenceLandingPage />} />
              <Route path="/user-registration" element={<UserRegistration />} />
              <Route path="/user-sign-in" element={<UserSignIn />} />
              <Route path="/check-email" element={<CheckEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Authenticated Routes - Dashboard */}
              <Route path="/user-dashboard" element={<UserDashboard />} />
              <Route path="/user-profile-management" element={<UserProfileManagement />} />
              <Route path="/notifications" element={<NotificationsInbox />} />
              <Route path="/billing" element={<BillingPage />} />
              
              {/* Conference Management — chair/organiser only.
                  Guarded at the route so the screen never mounts and never
                  fires its queries for someone without the role. Every one of
                  these was previously reachable by anyone who knew the URL: the
                  conference dashboard fetched the viewer's roles and only
                  logged them to the console, and the rest checked nothing.
                  RLS still governs reads and writes; this stops the interface
                  being shown at all. */}
              <Route path="/conference-setup-wizard" element={<ConferenceSetupWizard />} />
              <Route path="/conference-management-dashboard" element={<RequireConferenceRole manage><ConferenceManagementDashboard /></RequireConferenceRole>} />
              <Route path="/conference-submissions-overview" element={<RequireConferenceRole manage><ConferenceSubmissionsOverview /></RequireConferenceRole>} />
              <Route path="/submission-tracking-dashboard" element={<RequireConferenceRole manage><SubmissionTrackingDashboard /></RequireConferenceRole>} />
              <Route path="/review-schema-builder" element={<RequireConferenceRole manage><ReviewSchemaBuilder /></RequireConferenceRole>} />
              <Route path="/notification-management-screen" element={<RequireConferenceRole manage><NotificationManagementScreen /></RequireConferenceRole>} />
              <Route path="/email-template-management-system" element={<RequireConferenceRole manage><EmailTemplateManagementSystem /></RequireConferenceRole>} />
              <Route path="/manual-reviewer-assignment-interface" element={<RequireConferenceRole manage><ManualReviewerAssignmentInterface /></RequireConferenceRole>} />
              
              {/* Author Routes */}
              <Route path="/author-submission-portal" element={<AuthorSubmissionPortal />} />
              <Route path="/my-submissions" element={<MySubmissions />} />
              <Route path="/author-review-results-dashboard" element={<AuthorReviewResultsDashboard />} />
              
              {/* Reviewer Routes */}
              <Route path="/reviewer-dashboard" element={<ReviewerDashboard />} />
              <Route path="/paper-review-interface" element={<PaperReviewInterface />} />
              <Route path="/streamlined-review-workspace" element={<StreamlinedReviewWorkspace />} />
              
              {/* Admin Routes */}
              <Route path="/master-admin-dashboard" element={<MasterAdminDashboard />} />
              <Route path="/admin-conference-management-interface" element={<AdminConferenceManagementInterface />} />
              <Route path="/admin-user-management-system" element={<AdminUserManagementSystem />} />
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </AuthRedirectHandler>
          </ConferenceProvider>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
